import { format } from "date-fns";
import { CalendarDays, CalendarIcon, Check, Clock3, X } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const HOURS = Array.from({ length: 12 }, (_, index) => String(index + 1).padStart(2, "0"));
const MINUTES = Array.from({ length: 60 }, (_, index) => String(index).padStart(2, "0"));
const MERIDIEMS = ["AM", "PM"] as const;

type PickerGranularity = "date" | "minute";

type DateTimePickerProps = {
  value: string;
  onChange: (value: string) => void;
  granularity?: PickerGranularity;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "default" | "sm";
  align?: "start" | "center" | "end";
};

function parsePickerValue(value: string, granularity: PickerGranularity): Date | undefined {
  if (!value) return undefined;

  if (granularity === "date") {
    const [year, month, day] = value.split("-").map((part) => Number(part));
    if (!year || !month || !day) return undefined;
    return new Date(year, month - 1, day);
  }

  const [datePart, timePart = "00:00"] = value.split("T");
  if (!datePart) return undefined;
  const [year, month, day] = datePart.split("-").map((part) => Number(part));
  const [hours, minutes] = timePart.split(":").map((part) => Number(part));

  if (!year || !month || !day || Number.isNaN(hours) || Number.isNaN(minutes)) {
    return undefined;
  }

  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

function toDateValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toDateTimeValue(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toHour12(hours24: number): { hour: string; meridiem: (typeof MERIDIEMS)[number] } {
  const meridiem = hours24 >= 12 ? "PM" : "AM";
  const hour = hours24 % 12 || 12;
  return { hour: String(hour).padStart(2, "0"), meridiem };
}

function toHour24(hour12: string, meridiem: (typeof MERIDIEMS)[number]): number {
  const parsedHour = Number(hour12);
  if (meridiem === "AM") {
    return parsedHour === 12 ? 0 : parsedHour;
  }
  return parsedHour === 12 ? 12 : parsedHour + 12;
}

function getRoundedNow(): Date {
  const next = new Date();
  next.setSeconds(0, 0);
  const nextQuarterHour = Math.ceil(next.getMinutes() / 15) * 15;
  next.setMinutes(nextQuarterHour, 0, 0);
  return next;
}

function formatTriggerLabel(date: Date, granularity: PickerGranularity): string {
  return granularity === "date" ? format(date, "MMM d, yyyy") : format(date, "MMM d, yyyy, h:mm aa");
}

function DateTimePicker({
  value,
  onChange,
  granularity = "minute",
  placeholder,
  className,
  disabled,
  size = "default",
  align = "start",
}: DateTimePickerProps) {
  const parsedValue = React.useMemo(() => parsePickerValue(value, granularity), [granularity, value]);
  const [open, setOpen] = React.useState(false);
  const [draftDate, setDraftDate] = React.useState<Date | undefined>(parsedValue);
  const [draftHour, setDraftHour] = React.useState("12");
  const [draftMinute, setDraftMinute] = React.useState("00");
  const [draftMeridiem, setDraftMeridiem] = React.useState<(typeof MERIDIEMS)[number]>("PM");

  const syncDraftFromValue = React.useCallback(() => {
    const nextParsedValue = parsePickerValue(value, granularity);
    const fallback = getRoundedNow();
    const source = nextParsedValue ?? fallback;
    const { hour, meridiem } = toHour12(source.getHours());

    setDraftDate(nextParsedValue);
    setDraftHour(hour);
    setDraftMinute(String(source.getMinutes()).padStart(2, "0"));
    setDraftMeridiem(meridiem);
  }, [granularity, value]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (nextOpen) {
        syncDraftFromValue();
      }
    },
    [syncDraftFromValue],
  );

  const handleClear = React.useCallback(() => {
    onChange("");
    setOpen(false);
  }, [onChange]);

  const handleDateSelect = React.useCallback(
    (nextDate?: Date) => {
      if (!nextDate) {
        setDraftDate(undefined);
        if (granularity === "date") {
          onChange("");
          setOpen(false);
        }
        return;
      }

      if (granularity === "date") {
        onChange(toDateValue(nextDate));
        setOpen(false);
        return;
      }

      setDraftDate(nextDate);
    },
    [granularity, onChange],
  );

  const handleDone = React.useCallback(() => {
    if (!draftDate) return;
    const nextValue = new Date(draftDate);
    nextValue.setHours(toHour24(draftHour, draftMeridiem), Number(draftMinute), 0, 0);
    onChange(toDateTimeValue(nextValue));
    setOpen(false);
  }, [draftDate, draftHour, draftMeridiem, draftMinute, onChange]);

  const label = parsedValue
    ? formatTriggerLabel(parsedValue, granularity)
    : placeholder ?? (granularity === "date" ? "Pick a date" : "Pick a date and time");
  const TriggerIcon = granularity === "date" ? CalendarDays : CalendarIcon;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size={size}
          disabled={disabled}
          className={cn(
            "justify-between font-normal",
            size === "default" && "w-full",
            !parsedValue && "text-muted-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2 text-left">
            <TriggerIcon className="size-4 shrink-0 opacity-70" />
            <span className="truncate">{label}</span>
          </span>
          <Clock3 className={cn("size-4 shrink-0 opacity-50", granularity === "date" && "hidden")} />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className={cn("w-auto p-0", granularity === "minute" && "min-w-[320px]")}>
        <div className={cn("p-3", granularity === "minute" && "flex flex-col gap-4 sm:flex-row sm:items-start")}>
          <Calendar mode="single" selected={draftDate} onSelect={handleDateSelect} initialFocus />

          {granularity === "minute" ? (
            <div className="flex w-full min-w-[180px] flex-col gap-3 sm:pt-3">
              <div className="space-y-2">
                <p className="text-muted-foreground flex items-center gap-2 text-xs font-medium uppercase tracking-wide">
                  <Clock3 className="size-3.5" />
                  Time
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <Select value={draftHour} onValueChange={setDraftHour} disabled={!draftDate}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Hour" />
                    </SelectTrigger>
                    <SelectContent>
                      {HOURS.map((hour) => (
                        <SelectItem key={hour} value={hour}>
                          {hour}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={draftMinute} onValueChange={setDraftMinute} disabled={!draftDate}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Min" />
                    </SelectTrigger>
                    <SelectContent>
                      {MINUTES.map((minute) => (
                        <SelectItem key={minute} value={minute}>
                          {minute}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={draftMeridiem} onValueChange={(next) => setDraftMeridiem(next as (typeof MERIDIEMS)[number])} disabled={!draftDate}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="AM/PM" />
                    </SelectTrigger>
                    <SelectContent>
                      {MERIDIEMS.map((meridiem) => (
                        <SelectItem key={meridiem} value={meridiem}>
                          {meridiem}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 border-t pt-3">
                <Button type="button" variant="ghost" size="sm" onClick={handleClear}>
                  <X className="size-4" />
                  Clear
                </Button>
                <Button type="button" size="sm" onClick={handleDone} disabled={!draftDate}>
                  <Check className="size-4" />
                  Done
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-t px-3 pb-3 pt-0">
              <Button type="button" variant="ghost" size="sm" onClick={handleClear} disabled={!value}>
                <X className="size-4" />
                Clear
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { DateTimePicker };
